import React, { useState, useEffect, useMemo } from 'react';
import { doc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../auth/components/AuthProvider';
import * as SolicitudesProductoCampanaService from '../../repuestos/services/solicitudesProductoCampanaService';

function formatearFecha(fecha) {
  if (!fecha) return '—';
  if (typeof fecha?.toDate === 'function') return fecha.toDate().toLocaleDateString('es-ES');
  if (fecha?.seconds) return new Date(fecha.seconds * 1000).toLocaleDateString('es-ES');
  if (typeof fecha === 'string' || typeof fecha === 'number') return new Date(fecha).toLocaleDateString('es-ES');
  return '—';
}

function getSolicitudCampanaTitulo(s) {
  return s?.titulo || s?.nombre || s?.nombre_campana || 'Sin título';
}

function getSolicitudCampanaImagenUrl(s) {
  if (Array.isArray(s?.imagenes) && s.imagenes.length > 0 && s.imagenes[0]) return s.imagenes[0];
  if (s?.imagenUrl) return s.imagenUrl;
  if (s?.bannerUrl) return s.bannerUrl;
  if (s?.imagenBanner) return s.imagenBanner;
  return null;
}

/** Valor para input type="date" desde Firestore o string ISO. */
function toInputDateValue(fecha) {
  if (!fecha) return '';
  let d;
  if (typeof fecha?.toDate === 'function') d = fecha.toDate();
  else if (fecha?.seconds != null) d = new Date(fecha.seconds * 1000);
  else d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** Ficha en vivo: datos guardados + borrador de edición. */
function mergeSolicitudParaPreview(base, edit) {
  if (!base) return base;
  if (!edit) return base;
  const pres =
    edit.presupuesto === ''
      ? base.presupuesto
      : Number(edit.presupuesto);
  return {
    ...base,
    titulo: edit.titulo,
    nombre: edit.titulo,
    nombre_campana: edit.titulo,
    descripcion: edit.descripcion,
    presupuesto: Number.isFinite(pres) ? pres : base.presupuesto,
    tipo: edit.tipo,
    fechaInicio: edit.fechaInicio || base.fechaInicio,
    fechaFin: edit.fechaFin || base.fechaFin,
  };
}

/** Vista previa tipo ficha de campaña (modal admin). */
function SolicitudCampanaFichaPreview({ s }) {
  const img = getSolicitudCampanaImagenUrl(s);
  const titulo = getSolicitudCampanaTitulo(s);
  const empresa =
    s?.empresaNombre || s?.nombre_empresa || s?.nombre_proveedor || s?.empresaId || '—';
  const presupuesto = s?.presupuesto != null ? Number(s.presupuesto) : null;
  const ini = formatearFecha(s?.fechaInicio);
  const fin = formatearFecha(s?.fechaFin);
  return (
    <div className="rounded-2xl border border-violet-200 bg-gradient-to-b from-violet-50/80 to-white overflow-hidden shadow-inner mb-6">
      <div className="grid md:grid-cols-2 gap-0">
        <div className="aspect-[4/3] md:aspect-auto md:min-h-[180px] bg-violet-100/50 relative">
          {img ? (
            <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-violet-400 text-sm font-medium">
              Sin imagen / banner
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col justify-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">Vista previa — ficha campaña</p>
          <h3 className="text-xl font-bold text-gray-900 leading-tight">{titulo}</h3>
          <p className="text-sm text-gray-500 mt-1">{empresa}</p>
          {presupuesto != null && !Number.isNaN(presupuesto) && (
            <p className="text-lg font-semibold text-gray-900 mt-3">
              Presupuesto: ${presupuesto.toLocaleString('es-CL')}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            {s?.tipo && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-900">{s.tipo}</span>
            )}
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              {ini} → {fin}
            </span>
          </div>
        </div>
      </div>
      {s?.descripcion && (
        <div className="px-5 pb-5 pt-0 border-t border-violet-100 bg-white">
          <p className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-1">Descripción</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{s.descripcion}</p>
        </div>
      )}
    </div>
  );
}

function AdminSolicitudesCampana({ embedded = false, initialFilter = 'todos' }) {
  const { user } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [detalleSolicitud, setDetalleSolicitud] = useState(null);
  const [solicitudEditForm, setSolicitudEditForm] = useState(null);
  const [comentarioAdmin, setComentarioAdmin] = useState('');
  const [rechazoMotivo, setRechazoMotivo] = useState('');
  const [accionando, setAccionando] = useState(false);

  useEffect(() => {
    if (!detalleSolicitud) {
      setSolicitudEditForm(null);
      return;
    }
    const s = detalleSolicitud;
    setSolicitudEditForm({
      titulo: s.titulo || s.nombre || s.nombre_campana || '',
      descripcion: s.descripcion || '',
      presupuesto: s.presupuesto != null && s.presupuesto !== '' ? String(s.presupuesto) : '',
      tipo: s.tipo || 'promocional',
      fechaInicio: toInputDateValue(s.fechaInicio),
      fechaFin: toInputDateValue(s.fechaFin),
    });
  }, [detalleSolicitud?.id]);

  useEffect(() => {
    cargarSolicitudes();
  }, [filter]);

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      const data = await SolicitudesProductoCampanaService.fetchSolicitudesCampanaAdmin(filter);
      setSolicitudes(data);
    } catch (error) {
      console.error('Error cargando solicitudes de campañas:', error);
      setSolicitudes([]);
    } finally {
      setLoading(false);
    }
  };

  const puedeActivarRechazar = (s) =>
    s.estado === 'pendiente' || s.estado === 'en_revision';

  const previewMerged = useMemo(
    () =>
      detalleSolicitud
        ? mergeSolicitudParaPreview(detalleSolicitud, solicitudEditForm)
        : null,
    [detalleSolicitud, solicitudEditForm]
  );

  const persistEdicionSolicitudCampanaOrThrow = async () => {
    if (!detalleSolicitud?.id || !solicitudEditForm) return;
    if (!puedeActivarRechazar(detalleSolicitud)) return;
    const titulo = (solicitudEditForm.titulo || '').trim();
    if (!titulo) throw new Error('El título es obligatorio.');
    if (!solicitudEditForm.fechaInicio || !solicitudEditForm.fechaFin) {
      throw new Error('Indica fecha de inicio y fin.');
    }
    const ini = new Date(solicitudEditForm.fechaInicio);
    const fin = new Date(solicitudEditForm.fechaFin);
    if (fin < ini) {
      throw new Error('La fecha de fin debe ser posterior o igual a la de inicio.');
    }
    await SolicitudesProductoCampanaService.actualizarCamposSolicitudCampanaAdmin(
      detalleSolicitud.id,
      {
        titulo,
        descripcion: solicitudEditForm.descripcion,
        presupuesto: solicitudEditForm.presupuesto === '' ? 0 : Number(solicitudEditForm.presupuesto),
        tipo: solicitudEditForm.tipo,
        fechaInicio: solicitudEditForm.fechaInicio,
        fechaFin: solicitudEditForm.fechaFin,
      },
      user?.uid
    );
  };

  const handleAprobar = async () => {
    if (!detalleSolicitud?.id) return;
    setAccionando(true);
    try {
      await persistEdicionSolicitudCampanaOrThrow();
      await SolicitudesProductoCampanaService.aprobarSolicitudCampana(
        detalleSolicitud.id,
        comentarioAdmin || null,
        user?.uid,
        detalleSolicitud
      );
      setDetalleSolicitud(null);
      setComentarioAdmin('');
      setRechazoMotivo('');
      await cargarSolicitudes();
      alert('Solicitud activada. La campaña está publicada.');
    } catch (error) {
      console.error('Error aprobando solicitud:', error);
      const msg = error?.message || '';
      if (msg.includes('límite') || msg.includes('resource-exhausted')) {
        alert('No se pudo crear la campaña: la empresa alcanzó el límite de campañas de su plan.');
      } else {
        alert('Error al activar la solicitud: ' + msg);
      }
    } finally {
      setAccionando(false);
    }
  };

  const comentarioEmpresaActual = (s) => s?.comentarioAdmin || s?.comentariosAdmin || '';

  const refrescarDetalleSolicitud = async (solicitudId) => {
    const snap = await getDoc(doc(db, 'solicitudes_campana', solicitudId));
    if (snap.exists()) {
      setDetalleSolicitud({ id: snap.id, ...snap.data() });
    }
  };

  const handleGuardarEdicionSolicitud = async () => {
    if (!detalleSolicitud?.id || !solicitudEditForm) return;
    if (!puedeActivarRechazar(detalleSolicitud)) {
      alert('Solo se pueden editar solicitudes pendientes o en revisión.');
      return;
    }
    setAccionando(true);
    try {
      await persistEdicionSolicitudCampanaOrThrow();
      await refrescarDetalleSolicitud(detalleSolicitud.id);
      await cargarSolicitudes();
      alert('Cambios de la solicitud guardados.');
    } catch (error) {
      console.error(error);
      alert(error?.message || 'No se pudieron guardar los cambios.');
    } finally {
      setAccionando(false);
    }
  };

  const handleGuardarComentarioRevision = async () => {
    if (!detalleSolicitud?.id) return;
    const texto = (comentarioAdmin || '').trim();
    if (!texto) {
      alert('Escribe un comentario para la empresa antes de guardar.');
      return;
    }
    setAccionando(true);
    try {
      await persistEdicionSolicitudCampanaOrThrow();
      await SolicitudesProductoCampanaService.guardarComentarioEmpresaCampanaEnRevision(
        detalleSolicitud.id,
        texto,
        user?.uid
      );
      setDetalleSolicitud((prev) =>
        prev
          ? {
              ...prev,
              estado: 'en_revision',
              comentarioAdmin: texto,
              comentariosAdmin: texto,
            }
          : null
      );
      await cargarSolicitudes();
      alert('Comentario guardado. La solicitud quedó en revisión; la empresa lo verá en su dashboard.');
    } catch (error) {
      console.error(error);
      alert(error?.message || 'No se pudo guardar el comentario.');
    } finally {
      setAccionando(false);
    }
  };

  const handleRechazar = async () => {
    if (!detalleSolicitud?.id) return;
    setAccionando(true);
    try {
      await persistEdicionSolicitudCampanaOrThrow();
      await SolicitudesProductoCampanaService.rechazarSolicitudCampana(
        detalleSolicitud.id,
        rechazoMotivo || null,
        comentarioAdmin || null
      );
      setDetalleSolicitud(null);
      setComentarioAdmin('');
      setRechazoMotivo('');
      await cargarSolicitudes();
      alert('Solicitud rechazada.');
    } catch (error) {
      console.error('Error rechazando solicitud:', error);
      alert('Error al rechazar la solicitud');
    } finally {
      setAccionando(false);
    }
  };

  const eliminarSolicitud = async (solicitudId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta solicitud de campaña?')) return;
    try {
      await deleteDoc(doc(db, 'solicitudes_campana', solicitudId));
      if (detalleSolicitud?.id === solicitudId) setDetalleSolicitud(null);
      await cargarSolicitudes();
      alert('Solicitud eliminada exitosamente');
    } catch (error) {
      console.error('Error eliminando solicitud:', error);
      alert('Error al eliminar la solicitud');
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente': return 'bg-yellow-100 text-yellow-800';
      case 'en_revision': return 'bg-blue-100 text-blue-800';
      case 'aprobado':
      case 'activa': return 'bg-green-100 text-green-800';
      case 'rechazado':
      case 'rechazada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'pendiente': return 'Pendiente';
      case 'en_revision': return 'En Revisión';
      case 'aprobado':
      case 'activa': return estado === 'activa' ? 'Activada' : 'Aprobada';
      case 'rechazado':
      case 'rechazada': return 'Rechazada';
      default: return estado || 'Sin estado';
    }
  };

  if (loading && solicitudes.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">Cargando solicitudes de campañas...</span>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6'}>
      {!embedded && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800 font-medium">
            📢 <strong>Solicitudes de campañas</strong> — Revisa, activa o rechaza. Al activar se publica la campaña y la empresa ve el enlace en su dashboard. Comentario interno y motivo de rechazo opcionales.
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4 flex-wrap gap-2">
          <label className="text-sm font-medium text-gray-700">Filtrar por estado:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="todos">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="en_revision">En Revisión</option>
            <option value="activa">Activadas</option>
            <option value="aprobado">Aprobadas</option>
            <option value="rechazada">Rechazadas</option>
            <option value="rechazado">Rechazados</option>
          </select>
          <button
            onClick={cargarSolicitudes}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            🔄 Actualizar
          </button>
        </div>
      </div>

      {!embedded && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-yellow-600">
            {solicitudes.filter(s => s.estado === 'pendiente').length}
          </div>
          <div className="text-sm text-gray-600">Pendientes</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-600">
            {solicitudes.filter(s => s.estado === 'en_revision').length}
          </div>
          <div className="text-sm text-gray-600">En Revisión</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">
            {solicitudes.filter(s => s.estado === 'aprobado' || s.estado === 'activa').length}
          </div>
          <div className="text-sm text-gray-600">Activadas / Aprobadas</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-red-600">
            {solicitudes.filter(s => s.estado === 'rechazado' || s.estado === 'rechazada').length}
          </div>
          <div className="text-sm text-gray-600">Rechazadas</div>
        </div>
      </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className={`font-medium text-gray-900 ${embedded ? 'text-base' : 'text-lg'}`}>
            Solicitudes de campañas ({solicitudes.length})
          </h3>
          {embedded && (
            <p className="text-xs text-gray-500 mt-1">
              Puedes <strong>editar</strong> la solicitud (título, fechas, etc.), <strong>dejar comentario</strong> a la empresa y luego activar o rechazar. Al activar se publica la campaña con los datos guardados.
            </p>
          )}
        </div>
        {solicitudes.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No hay solicitudes de campañas para mostrar.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaña</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {solicitudes.map((solicitud) => (
                  <tr key={solicitud.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {solicitud.titulo || solicitud.nombre || solicitud.nombre_campana || 'Sin nombre'}
                        </div>
                        <div className="text-sm text-gray-500 line-clamp-2">{solicitud.descripcion || '—'}</div>
                        {solicitud.presupuesto != null && (
                          <div className="text-sm text-gray-500">${Number(solicitud.presupuesto).toLocaleString()}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {solicitud.empresaNombre || solicitud.nombre_empresa || solicitud.nombre_proveedor || '—'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {solicitud.email_proveedor || solicitud.email_empresa || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEstadoColor(solicitud.estado)}`}>
                        {getEstadoTexto(solicitud.estado)}
                      </span>
                      {solicitud.accesoUrl && (
                        <a href={solicitud.accesoUrl} target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 mt-1 truncate max-w-[180px]">
                          Ver publicado
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatearFecha(solicitud.fechaSolicitud)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setDetalleSolicitud(solicitud);
                            setComentarioAdmin(comentarioEmpresaActual(solicitud));
                            setRechazoMotivo(solicitud.rechazoMotivo || '');
                          }}
                          className="text-blue-600 hover:underline text-xs font-medium"
                        >
                          Previsualizar ficha
                        </button>
                        {puedeActivarRechazar(solicitud) && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setDetalleSolicitud(solicitud);
                                setComentarioAdmin('');
                                setRechazoMotivo('');
                              }}
                              className="text-green-600 hover:underline text-xs font-medium"
                            >
                              Activar
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setDetalleSolicitud(solicitud);
                                setRechazoMotivo('');
                                setComentarioAdmin(comentarioEmpresaActual(solicitud));
                              }}
                              className="text-red-600 hover:underline text-xs font-medium"
                            >
                              Rechazar
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => eliminarSolicitud(solicitud.id)}
                          className="text-gray-500 hover:text-gray-700 text-xs"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal detalle */}
      {detalleSolicitud && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Detalle: {detalleSolicitud.titulo || detalleSolicitud.nombre || detalleSolicitud.nombre_campana || 'Solicitud'}
                </h2>
                <button
                  type="button"
                  onClick={() => { setDetalleSolicitud(null); setComentarioAdmin(''); setRechazoMotivo(''); setSolicitudEditForm(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {puedeActivarRechazar(detalleSolicitud) && solicitudEditForm && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 mb-6 space-y-3">
                  <p className="text-sm font-semibold text-amber-900">Editar solicitud</p>
                  <p className="text-xs text-amber-800/90">
                    Los cambios se guardan en Firestore. La vista previa de abajo refleja el borrador hasta que pulses &quot;Guardar cambios&quot;.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block sm:col-span-2 text-sm">
                      <span className="font-medium text-gray-700">Título</span>
                      <input
                        type="text"
                        value={solicitudEditForm.titulo}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, titulo: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block sm:col-span-2 text-sm">
                      <span className="font-medium text-gray-700">Descripción</span>
                      <textarea
                        value={solicitudEditForm.descripcion}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, descripcion: e.target.value }))}
                        rows={3}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Presupuesto</span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={solicitudEditForm.presupuesto}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, presupuesto: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Tipo</span>
                      <input
                        type="text"
                        value={solicitudEditForm.tipo}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, tipo: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="promocional"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Inicio</span>
                      <input
                        type="date"
                        value={solicitudEditForm.fechaInicio}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, fechaInicio: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="font-medium text-gray-700">Fin</span>
                      <input
                        type="date"
                        value={solicitudEditForm.fechaFin}
                        onChange={(e) => setSolicitudEditForm((f) => ({ ...f, fechaFin: e.target.value }))}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={handleGuardarEdicionSolicitud}
                    disabled={accionando}
                    className="px-4 py-2 bg-amber-700 text-white text-sm font-medium rounded-lg hover:bg-amber-800 disabled:opacity-50"
                  >
                    {accionando ? 'Guardando…' : 'Guardar cambios de la solicitud'}
                  </button>
                </div>
              )}

              <SolicitudCampanaFichaPreview s={previewMerged} />

              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Datos técnicos</p>
              <div className="space-y-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Empresa / ID:</span>{' '}
                  {detalleSolicitud.empresaNombre || detalleSolicitud.nombre_empresa || detalleSolicitud.empresaId}
                </div>
                <div>
                  <span className="font-medium text-gray-700">Título:</span>{' '}
                  {getSolicitudCampanaTitulo(previewMerged) || '—'}
                </div>
                <div className="flex flex-wrap gap-4">
                  {previewMerged?.presupuesto != null &&
                    Number.isFinite(Number(previewMerged.presupuesto)) && (
                    <span>
                      <strong>Presupuesto:</strong> ${Number(previewMerged.presupuesto).toLocaleString()}
                    </span>
                  )}
                  {previewMerged?.tipo && (
                    <span><strong>Tipo:</strong> {previewMerged.tipo}</span>
                  )}
                  {previewMerged?.fechaInicio && (
                    <span><strong>Inicio:</strong> {formatearFecha(previewMerged.fechaInicio)}</span>
                  )}
                  {previewMerged?.fechaFin && (
                    <span><strong>Fin:</strong> {formatearFecha(previewMerged.fechaFin)}</span>
                  )}
                </div>
                {detalleSolicitud.rechazoMotivo && (
                  <div className="bg-red-50 border border-red-200 rounded p-2">
                    <span className="font-medium text-red-800">Motivo rechazo:</span>
                    <p className="text-red-700">{detalleSolicitud.rechazoMotivo}</p>
                  </div>
                )}
                {detalleSolicitud.accesoUrl && (
                  <div>
                    <span className="font-medium text-gray-700">Enlace publicado:</span>
                    <a href={detalleSolicitud.accesoUrl} target="_blank" rel="noopener noreferrer" className="block text-blue-600 truncate">
                      {detalleSolicitud.accesoUrl}
                    </a>
                  </div>
                )}
                <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 space-y-2">
                  <label className="block font-medium text-gray-800 mb-1">
                    Comentario para la empresa <span className="text-gray-500 font-normal">(visible en su dashboard)</span>
                  </label>
                  <p className="text-xs text-gray-600">
                    Si guardas el comentario con el botón inferior, la solicitud pasa automáticamente a <strong>En revisión</strong> hasta que la actives o rechaces.
                  </p>
                  <textarea
                    value={comentarioAdmin}
                    onChange={(e) => setComentarioAdmin(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                    placeholder="Ej: Ajusta las fechas de la campaña o sube una imagen más clara del banner…"
                  />
                  {puedeActivarRechazar(detalleSolicitud) && (
                    <button
                      type="button"
                      onClick={handleGuardarComentarioRevision}
                      disabled={accionando}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {accionando ? 'Guardando…' : 'Guardar comentario y dejar en revisión'}
                    </button>
                  )}
                </div>
                {puedeActivarRechazar(detalleSolicitud) && (
                  <div>
                    <label className="block font-medium text-gray-700 mb-1">Motivo de rechazo (si rechaza)</label>
                    <textarea
                      value={rechazoMotivo}
                      onChange={(e) => setRechazoMotivo(e.target.value)}
                      rows={2}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="Motivo visible para la empresa"
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t">
                {puedeActivarRechazar(detalleSolicitud) && (
                  <>
                    <p className="w-full text-xs text-gray-500 -mt-1 mb-1">
                      Al <strong>activar</strong> o <strong>rechazar</strong> también puedes dejar un comentario arriba; en activación se guarda junto con la publicación.
                    </p>
                    <button
                      type="button"
                      onClick={handleAprobar}
                      disabled={accionando}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {accionando ? 'Procesando...' : '✅ Activar y publicar'}
                    </button>
                    <button
                      type="button"
                      onClick={handleRechazar}
                      disabled={accionando}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {accionando ? '...' : '❌ Rechazar'}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => { setDetalleSolicitud(null); setComentarioAdmin(''); setRechazoMotivo(''); setSolicitudEditForm(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSolicitudesCampana;
